import { useContext, useState } from 'react';
import { BreadCrumb } from '../../components/breadcrumbs/breadcrumbs';
import Icon from '../../components/icons/icon';
import { Typography } from '../../components/typography/typography';
import './inventory-add-edit.scss';
import { TenantContext } from '../../context/tenant-context';
import { Card } from '../../components/card/card';
import { Button } from '../../components/form/button';
import { Divider } from '../../components/divider/divider';
import { useAddProductData } from './hooks/use-add-product-data';
import { ProductInventory } from './components/product-inventory/product-inventory';
import { useParams } from 'react-router-dom';
import { ProductPricing } from './components/product-pricing/product-pricing';
import { ProductTaxes } from './components/product-taxes/product-taxes';
import { Modal } from '../../components/modal/modal';
import SkeletonLoader from '../../components/skeleton-loader/skeleton-loader';
import { useAdmin } from '../../hooks/use-admin-context';
import { Constants } from '../../constants/constants';
import { SelectForm } from '../../components/select-form/select-form';
import { Toggle } from '../../components/form/toggle';
import { PrettyDescriptor } from '../../components/pretty-descriptor/pretty-descriptor';
import { ProductDelivery } from './components/product-delivery/product-delivery';

export const InventoryAddEdit = ({ type = 'Part' }: { type?: 'Part' | 'Service' }) => {
  const { contextPath } = useContext(TenantContext);

  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const admin = useAdmin();

  const canEditTax = admin.hasPermissions(Constants.Permissions.Accounting);

  const {
    title,
    handleBlur,
    setTitle,
    productNumber,
    setProductNumber,
    selectedLocationIds,
    setSelectedLocationIds,
    productInventory,
    setProductInventory,
    productPricing,
    setProductPricing,
    globalDeliveryConfig,
    productDeliveryConfig,
    setProductDeliveryConfig,
    chargeTax,
    setChargeTax,
    taxCategoryId,
    setTaxCategoryId,
    allLocations,
    isSaving,
    isLoading,
    isInitiallyLoading,
    currency,
    taxCategories,
    glAccounts,
    createProductCall,
    deletePriceCall,
    purchasePrice,
    setPurchasePrice,
    deleteProductCall,
    productCategoryIds,
    setProductCategoryIds,
    categories,
    isActive,
    toggleIsActive,
  } = useAddProductData({ id, type, canEditTax });

  const crudActionLabel = (isEditing ? 'Edit ' : 'Add ') + type;

  const typePlural = type === 'Part' ? 'Parts' : 'Services';
  const previousUrl = `${contextPath}manager/inventory/${typePlural.toLowerCase()}`;
  const breadcrumbs = [
    { label: 'Inventory' },
    { label: typePlural, link: previousUrl },
    {
      label: crudActionLabel,
    },
  ];

  return (
    <div className="add-part overflow-x-auto w-full">
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onDelete={async () => {
          await deleteProductCall();
          setIsDeleteModalOpen(false);
        }}
        isDeleting={isLoading}
        productType={type}
      />
      {/* Bread crumbs */}
      {breadcrumbs && <BreadCrumb breadcrumbs={breadcrumbs} />}

      <div className="add-part-header gap-2">
        <div className="flex gap-2">
          <a className="cursor-pointer" href={previousUrl}>
            <Icon icon="Back" size="large" />
          </a>
          <Typography type="text-xl" weight="semi-bold">
            {crudActionLabel}
          </Typography>
        </div>
        <div className="crud-actions gap-2">
          {!isEditing && (
            <Button
              className="button button-yellow md"
              data-cy="save_part"
              onClick={() => {
                if (!isEditing) {
                  createProductCall();
                }
              }}
            >
              <Icon icon="FloppySave" size="medium" stroke="var(--quipli-black-800)" />
              <Typography type="text-md" weight="semi-bold" className="text-[var(--quipli-black-800)]">
                {`Create ${type}`}
              </Typography>
            </Button>
          )}
        </div>
      </div>
      <div className="crud-body gap-4">
        {/* Title and CRUD Actions */}
        <Card className="add-part-title-card card gap-4">
          <div className="title-and-actions">
            <Typography type="text-xl" weight="semi-bold">
              {type} Details
            </Typography>
          </div>
          <Divider />
          <div className="part-title-input">
            {isInitiallyLoading ? (
              <SkeletonLoader style={{ width: '--webkit-fill-available', height: '10vh' }} />
            ) : (
              <>
                <Typography className="flex flex-row items-center" type="text-md" weight="regular">
                  {type} Name <p className="required">*</p>
                </Typography>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onBlur={(e) => handleBlur('name', e.target.value)}
                  className="w-full md:max-w-[40vw]"
                  disabled={isSaving || isLoading}
                  data-cy="part-title"
                />
              </>
            )}
          </div>
          <Divider />
          <div className="part-title-input">
            {isInitiallyLoading ? (
              <SkeletonLoader style={{ width: '--webkit-fill-available', height: '10vh' }} />
            ) : (
              <>
                <Typography className="flex flex-row items-center" type="text-md" weight="regular">
                  Categories
                </Typography>
                <SelectForm
                  isMulti
                  value={categories.filter((_) => productCategoryIds.includes(_.id))}
                  onBlur={(e) => handleBlur('categoryIds', e.target.value)}
                  options={categories}
                  getOptionLabel={(c) => c.name}
                  getOptionValue={(c) => c.id.toString()}
                  onChange={(e) => {
                    const selectedIds = e.map((category) => category.id);
                    setProductCategoryIds(selectedIds);
                  }}
                  isSearchable={true}
                  menuShouldBlockScroll
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                ></SelectForm>
              </>
            )}
          </div>
          <div className="flex flex-row gap-2">
            {isInitiallyLoading ? (
              <SkeletonLoader style={{ width: '--webkit-fill-available', height: '10vh' }} />
            ) : (
              <>
                <div className="flex flex-col lg:flex-row lg:items-center max-w-[28rem]">
                  <PrettyDescriptor
                    title="Visible On Storefront"
                    description={`Controls whether this part is shown on your storefront. Hidden parts can still be added to orders and work orders in the admin console.`}
                  />
                </div>

                <div className="flex flex-col lg:flex-row lg:items-center">
                  <Toggle
                    color="var(--quipli-yellow-300)"
                    data-cy="part-visible-toggle"
                    checked={isActive}
                    onClick={() => {
                      toggleIsActive();
                    }}
                    disabled={isSaving}
                  />
                </div>
              </>
            )}
          </div>
        </Card>

        {/* Part Inventory :) */}
        {(type === 'Part' || isEditing) && (
          <ProductInventory
            productNumber={productNumber}
            setProductNumber={setProductNumber}
            selectedLocationIds={selectedLocationIds}
            setSelectedLocationIds={setSelectedLocationIds}
            productInventory={productInventory}
            setProductInventory={setProductInventory}
            allLocations={allLocations}
            isInitiallyLoading={isInitiallyLoading}
            isSaving={isSaving}
            handleBlur={handleBlur}
            type={type}
            isEditing={isEditing}
            purchasePrice={purchasePrice}
            setPurchasePrice={setPurchasePrice}
          />
        )}

        {/* We don't want these to be here for the initial creation :) */}
        {/* When we expand to products, may make sense to break this whole page into separate ones
            for parts, services, and product, so we can allow for some more flexibility, but definitely
            should re-use these main components and keep network calls abstracted still */}
        {isEditing && !isLoading && (
          <>
            {/* Part Pricing */}
            <ProductPricing
              productPricing={productPricing}
              setProductPricing={setProductPricing}
              currency={currency}
              allLocations={allLocations}
              isInitiallyLoading={isInitiallyLoading}
              isSaving={isSaving}
              selectedLocationIds={selectedLocationIds}
              deletePriceCall={deletePriceCall}
              type={type}
            />

            {/* Delivery & Pickup */}
            <ProductDelivery
              isSaving={isSaving}
              globalDeliveryConfig={globalDeliveryConfig!}
              productDeliveryConfig={productDeliveryConfig!}
              setProductDeliveryConfig={setProductDeliveryConfig}
            />

            {/* Taxes & Accounting */}
            {canEditTax && (
              <ProductTaxes
                productId={parseInt(id)}
                chargeTax={chargeTax}
                setChargeTax={setChargeTax}
                taxCategories={taxCategories}
                taxCategoryId={taxCategoryId}
                setTaxCategoryId={setTaxCategoryId}
                glAccounts={glAccounts}
                isInitiallyLoading={isInitiallyLoading}
                isSaving={isSaving}
                type={type}
              />
            )}
          </>
        )}
        <div className="flex flex-row w-full col-span-full justify-end gap-2">
          {isEditing && (
            <Button className="button-red flex flex-row" onClick={() => !isSaving && setIsDeleteModalOpen(true)}>
              <Icon icon="Trash" stroke="white" />
              <Typography type="text-md" weight="semi-bold">
                Delete {type}
              </Typography>
            </Button>
          )}
          {/* We're taking this out and moving towards auto-saving, but keeping this here in-case we need this pattern */}
          {/* <Button className="button-yellow flex flex-row" onClick={() => !isSaving && updateProductCall()}>
            <Icon icon="FloppySave" />
            <Typography type="text-md" weight="semi-bold">
              Save Changes
            </Typography>
          </Button> */}
        </div>
      </div>
    </div>
  );
};

export const absoluteSelectStyle = {
  menu: (provided: any) => ({
    ...provided,
    position: 'absolute',
    top: '100%',
    left: 0,
    zIndex: 1000, // Ensure the menu is on top
  }),
};

export const DeleteModal = ({
  isOpen,
  onClose,
  onDelete,
  isDeleting,
  productType,
}: {
  isOpen: boolean;
  onClose: () => void;
  onDelete: () => void;
  isDeleting: boolean;
  productType: string;
}) => {
  return (
    <Modal
      title={`Delete this ${productType}?`}
      isOpen={isOpen}
      close={onClose}
      headerIcon={<Icon icon="Trash" size="medium" />}
      actions={
        <>
          <Button
            className="button button-secondary md"
            onClick={onClose}
            disabled={isDeleting}
            data-cy="cancel_delete"
          >
            <Icon icon="Close" size="medium" stroke="var(--quipli-black-800)" />
            <Typography type="text-md" weight="semi-bold" className="text-[var(--quipli-black-800)]">
              Cancel
            </Typography>
          </Button>
          <Button className="button button-red md" onClick={onDelete} disabled={isDeleting} data-cy="confirm_delete">
            <Icon icon="Trash" size="medium" stroke="var(--quipli-white)" />
            <Typography type="text-md" weight="semi-bold" className="text-[var(--quipli-white)]">
              Delete {productType}
            </Typography>
          </Button>
        </>
      }
    >
      <Typography type="text-md" weight="regular">
        This action will permanently delete the {productType.toLowerCase()} and cannot be undone. Are you sure you want
        to continue?
      </Typography>
    </Modal>
  );
};
